// tableHub — ONE `postgres_changes` binding per (table, filter) per tab, shared
// by every consumer that wants that table, ref-counted.
//
// Why (REALTIME_REDESIGN_PLAN.md §1, rollout step 2): for every row that
// changes, Supabase Realtime runs the table's security policy once PER
// SUBSCRIBER, on one thread. Measured on production main (2026-09-21) a single
// dispatcher tab bound `messages` six times (NotificationHandler INSERT +
// UPDATE, CustomerReplyAlerts INSERT, the open chats) and `emails` four times
// (badge, alerts, cache, inbox). Six subscribers cost six policy runs per
// message for the same tab. Through the hub they cost one.
//
// How: the first consumer of a (table, filter) opens the channel, bound to
// event `*`; later consumers join it; the last one to leave removes it. Each
// consumer names the events it wants and the hub dispatches CLIENT-side by
// `payload.eventType`, so an INSERT-only consumer still never sees an UPDATE.
// Recovery is the shared `channelResync` policy: after a re-join the hub asks
// every consumer that cares (`onResync`) to re-read the truth, exactly as the
// per-consumer channel used to.
//
// Kill switch: TABLE_HUB_ENABLED=false restores one channel per consumer,
// bound to that consumer's own event (the pre-hub transport), in one edit and
// with no call-site change.
//
// Not a feature flag: this is a pure transport refactor — every consumer's
// callbacks, filters and timing are unchanged and the tests for each consumer
// prove it.
import { supabase, channelResync, type RealtimePayload, type ExternalTableControl } from './supabaseClient';

/** Set to false to restore one private channel per consumer (pre-hub transport). */
export const TABLE_HUB_ENABLED = true;

export type TableHubEvent = 'INSERT' | 'UPDATE' | 'DELETE';

export interface TableHubListener {
  /** One change on the table (a supabase-js postgres_changes payload). */
  onChange: (payload: RealtimePayload) => void;
  /** The channel re-joined after a drop (or stayed down a long time): re-read
   *  the truth. Leave it out to keep a consumer that never resynced before. */
  onResync?: () => void;
  /** Events this consumer wants. Default: all. A payload with no eventType
   *  (only a test double produces one; supabase-js always sets it) reaches
   *  every consumer. */
  events?: readonly TableHubEvent[];
}

export interface TableHubOptions {
  /** PostgREST filter for the CHANNEL (`conversation_id=eq.X`). Consumers with
   *  different filters never share a channel; see subscribeTable's
   *  `channelFilter` for when a channel filter is safe at all. */
  filter?: string;
}

interface Hub {
  key: string;
  table: string;
  filter?: string;
  listeners: Set<TableHubListener>;
  channel: any;
  /** The server-side binding: one event while every consumer wants the same
   *  single event (an INSERT-only tab never receives UPDATE/DELETE frames —
   *  on a REPLICA IDENTITY FULL table each carries two full rows and costs one
   *  policy check on the Realtime thread), `*` as soon as anyone wants more. */
  bound: '*' | TableHubEvent;
  /** Listeners present before a widening rebind: they get one resync when the
   *  wider channel is live, closing the gap the rebind opened. */
  pendingResync: Set<TableHubListener> | null;
}

const hubs = new Map<string, Hub>();

const rand = () => Math.random().toString(36).slice(2, 8);

/** The narrowest server-side binding that serves every listener. */
const bindingFor = (listeners: Iterable<TableHubListener>): '*' | TableHubEvent => {
  let only: TableHubEvent | null | undefined;
  for (const l of listeners) {
    if (!l.events || l.events.length !== 1) return '*';
    if (only === undefined) only = l.events[0];
    else if (only !== l.events[0]) return '*';
  }
  return only ?? '*';
};

const wants = (l: TableHubListener, payload: RealtimePayload | undefined): boolean => {
  if (!l.events) return true;
  const t = payload?.eventType;
  if (!t) return true;
  return (l.events as readonly string[]).includes(t);
};

const safely = (fn: () => void) => {
  // One bad consumer must not starve the rest — with a channel each, an
  // exception in one never reached another; the shared channel keeps that.
  try { fn(); } catch (e) { console.error('[tableHub] consumer threw', e); }
};

function openChannel(
  table: string,
  filter: string | undefined,
  event: '*' | TableHubEvent,
  onPayload: (p: RealtimePayload) => void,
  onStatus: (status: string) => void,
): any {
  // supabase-js types `on('postgres_changes', …)` as one overload per literal
  // event; a variable event needs the widened spec.
  const spec: any = { event, schema: 'public', table, ...(filter ? { filter } : {}) };
  return supabase()
    .channel(`hub_${table}_${rand()}`)
    .on('postgres_changes', spec, onPayload)
    .subscribe(onStatus);
}

function bind(h: Hub): void {
  const key = h.key;
  const status = channelResync(() => {
    if (hubs.get(key) !== h) return;
    for (const l of [...h.listeners]) safely(() => l.onResync?.());
  });
  h.channel = openChannel(
    h.table, h.filter, h.bound,
    (p) => {
      if (hubs.get(key) !== h) return;
      for (const l of [...h.listeners]) if (wants(l, p)) safely(() => l.onChange(p));
    },
    (s) => {
      if (hubs.get(key) !== h) return;
      if (s === 'SUBSCRIBED' && h.pendingResync) {
        const gap = h.pendingResync;
        h.pendingResync = null;
        for (const l of gap) if (h.listeners.has(l)) safely(() => l.onResync?.());
      }
      status(s);
    },
  );
}

/**
 * Follow `table` (optionally one channel filter) through the shared hub.
 * Returns leave(). Idempotent: calling leave twice is a no-op.
 */
export function subscribeTableHub(
  table: string,
  listener: TableHubListener,
  opts?: TableHubOptions,
): () => void {
  const filter = opts?.filter || undefined;

  if (!TABLE_HUB_ENABLED) {
    // Pre-hub transport: a private channel bound to this consumer's own event.
    const event = listener.events && listener.events.length === 1 ? listener.events[0] : '*';
    let dead = false;
    const channel = openChannel(
      table, filter, event,
      (p) => { if (!dead && wants(listener, p)) safely(() => listener.onChange(p)); },
      channelResync(() => { if (!dead) safely(() => listener.onResync?.()); }),
    );
    return () => {
      if (dead) return;
      dead = true;
      supabase().removeChannel(channel);
    };
  }

  const key = `${table}|${filter ?? ''}`;
  let hub = hubs.get(key);
  if (!hub) {
    const h: Hub = { key, table, filter, listeners: new Set(), channel: null, bound: bindingFor([listener]), pendingResync: null };
    hubs.set(key, h);
    bind(h);
    hub = h;
  } else {
    const wider = bindingFor([...hub.listeners, listener]);
    if (wider !== hub.bound) {
      // A consumer that needs more than the channel carries: rebind wider.
      // The listeners already here may miss a change during the swap, so they
      // resync once the wider channel is live.
      const h = hub;
      supabase().removeChannel(h.channel);
      h.bound = wider;
      h.pendingResync = new Set(h.listeners);
      bind(h);
    }
  }
  const mine = hub;
  mine.listeners.add(listener);
  let left = false;
  return () => {
    if (left) return;
    left = true;
    mine.listeners.delete(listener);
    if (mine.listeners.size === 0 && hubs.get(key) === mine) {
      hubs.delete(key);
      supabase().removeChannel(mine.channel);
    }
  };
}

/**
 * The hub as a `subscribeTable(..., { external })` transport: the list keeps
 * its cache-first paint, fetch, patching and debounce exactly as before; only
 * the channel is shared. Returns undefined when the hub is switched off, so
 * `external: hubTransport('emails')` falls straight back to subscribeTable's
 * own channel.
 */
export function hubTransport(
  table: string,
  filter?: string,
): ((ctl: ExternalTableControl) => () => void) | undefined {
  if (!TABLE_HUB_ENABLED) return undefined;
  return (ctl) => subscribeTableHub(
    table,
    { onChange: (p) => ctl.change(p), onResync: () => ctl.refetch() },
    { filter },
  );
}

/** Test seam: every open hub with its consumers' event interests. */
export function _tableHubState(): Array<{ key: string; table: string; filter?: string; bound: '*' | TableHubEvent; listeners: Array<{ events?: readonly TableHubEvent[] }> }> {
  return [...hubs.values()].map((h) => ({
    key: h.key,
    bound: h.bound,
    table: h.table,
    filter: h.filter,
    listeners: [...h.listeners].map((l) => ({ events: l.events })),
  }));
}

/** Test seam: drop every hub and its channel. */
export function _resetTableHub(): void {
  for (const h of hubs.values()) {
    try { supabase().removeChannel(h.channel); } catch { /* ignore */ }
  }
  hubs.clear();
}
